import type { ChatbotConfig } from "./types";

export interface LiveSiteCompatibilityConfig {
  socialChatSelectors: string[];
  backToTopSelector: string;
  themeProfile: string;
}

export interface LiveSiteCompatibilityState {
  hasSocialChat: boolean;
  hasBackToTop: boolean;
  themeProfile: string;
}

export interface LiveSitePlacementState {
  hasSocialChat: boolean;
  socialOffsetPx: number;
  socialCenterViewportX: number | null;
}

export const defaultCompatibilityConfig: LiveSiteCompatibilityConfig = {
  socialChatSelectors: [".qlwapp__container", ".qlwapp", "#qlwapp"],
  backToTopSelector: "#back-to-top",
  themeProfile: "engitech"
};

export const resolveCompatibilityConfig = (config: ChatbotConfig): LiveSiteCompatibilityConfig => ({
  socialChatSelectors: (() => {
    const selectors = config.compatibility?.socialChatSelectors?.filter((selector) => selector.trim().length > 0);
    return selectors?.length ? selectors : defaultCompatibilityConfig.socialChatSelectors;
  })(),
  backToTopSelector:
    config.compatibility?.backToTopSelector || defaultCompatibilityConfig.backToTopSelector,
  themeProfile: config.compatibility?.themeProfile || defaultCompatibilityConfig.themeProfile
});

const queryAny = (root: ParentNode, selectors: string[]): boolean =>
  queryFirst(root, selectors) !== null;

const queryFirst = (root: ParentNode, selectors: string[]): HTMLElement | null => {
  for (const selector of selectors) {
    try {
      const element = root.querySelector(selector);
      if (element instanceof HTMLElement) {
        return element;
      }
    } catch {
      // Ignore invalid selectors from localized compatibility config.
    }
  }

  return null;
};

export const detectLiveSiteCompatibility = (
  root: ParentNode,
  config: LiveSiteCompatibilityConfig
): LiveSiteCompatibilityState => ({
  hasSocialChat: queryAny(root, config.socialChatSelectors),
  hasBackToTop: queryAny(root, [config.backToTopSelector]),
  themeProfile: config.themeProfile
});

export const buildCompatibilityClassList = (state: LiveSiteCompatibilityState): string[] => [
  `is-theme-${state.themeProfile}`,
  ...(state.hasSocialChat ? ["has-social-chat-neighbor"] : []),
  ...(state.hasBackToTop ? ["has-back-to-top-neighbor"] : [])
];

const setStyles = (el: HTMLElement, props: Record<string, string>): void => {
  for (const [k, v] of Object.entries(props)) {
    el.style.setProperty(k, v);
  }
};

const removeStyles = (el: HTMLElement, props: string[]): void => {
  for (const p of props) {
    el.style.removeProperty(p);
  }
};

/**
 * Reads the WhatsApp icon's bounding rect and positions the chatbot
 * at the same vertical level and immediately to its right.
 */
export const updateLiveSitePlacement = (
  widget: HTMLElement,
  root: ParentNode,
  config: LiveSiteCompatibilityConfig
): LiveSitePlacementState => {
  // Match the WhatsApp icon specifically first, fall back to container
  const socialChat =
    queryFirst(root, [".qlwapp__icon.qlwapp-whatsapp-icon", ".qlwapp__icon"])
    ?? queryFirst(root, config.socialChatSelectors);
  if (!socialChat) {
    removeStyles(widget, ["top", "left", "bottom", "right", "transform",
      "--ptc-chatbot-social-offset", "--ptc-chatbot-social-center-x",
      "--ptc-chatbot-launcher-size", "--ptc-chatbot-base-bottom"]);
    const launcher = widget.querySelector(".ptc-chatbot__launcher") as HTMLElement | null;
    if (launcher) {
      removeStyles(launcher, ["transform", "left"]);
    }
    delete widget.dataset.ptcChatbotSocialSide;

    return {
      hasSocialChat: false,
      socialOffsetPx: 0,
      socialCenterViewportX: null
    };
  }

  const rect = socialChat.getBoundingClientRect();

  setStyles(widget, {
    top: `${Math.round(rect.top - 80)}px`,
    left: `${Math.round(rect.right - 38)}px`,
    bottom: "auto",
    transform: "translateX(-18px)",
  });

  const launcher = widget.querySelector(".ptc-chatbot__launcher") as HTMLElement | null;
  if (launcher) {
    setStyles(launcher, { transform: "scale(1.25)", left: "24px" });
  }

  removeStyles(widget, ["--ptc-chatbot-social-offset", "--ptc-chatbot-social-center-x",
    "--ptc-chatbot-launcher-size", "--ptc-chatbot-base-bottom"]);

  return {
    hasSocialChat: true,
    socialOffsetPx: 0,
    socialCenterViewportX: rect.left + rect.width / 2
  };
};

/**
 * Binds resize, scroll, and mutation listeners once per widget element so
 * repeated Vue mounts or compatibility refreshes do not duplicate work.
 */
export const bindLiveSitePlacement = (
  widget: HTMLElement,
  root: ParentNode,
  config: LiveSiteCompatibilityConfig
): (() => void) => {
  if (widget.dataset.ptcChatbotPlacementBound === "true") {
    return () => undefined;
  }

  widget.dataset.ptcChatbotPlacementBound = "true";
  const browserWindow = widget.ownerDocument.defaultView ?? window;
  let frame = 0;
  const schedule = (callback: FrameRequestCallback): number =>
    typeof browserWindow.requestAnimationFrame === "function"
      ? browserWindow.requestAnimationFrame(callback)
      : browserWindow.setTimeout(() => callback(Date.now()), 16);
  const cancel = (handle: number): void => {
    if (typeof browserWindow.cancelAnimationFrame === "function") {
      browserWindow.cancelAnimationFrame(handle);
      return;
    }

    browserWindow.clearTimeout(handle);
  };

  const refresh = (): void => {
    if (frame !== 0) {
      return;
    }

    frame = schedule(() => {
      frame = 0;
      updateLiveSitePlacement(widget, root, config);
    });
  };

  const observer =
    typeof MutationObserver !== "undefined" && widget.ownerDocument.body
      ? new MutationObserver(refresh)
      : null;

  updateLiveSitePlacement(widget, root, config);
  browserWindow.addEventListener("resize", refresh, { passive: true });
  browserWindow.addEventListener("scroll", refresh, { passive: true });
  observer?.observe(widget.ownerDocument.body, {
    attributes: true,
    childList: true,
    subtree: true,
    attributeFilter: ["class", "style"]
  });

  return () => {
    if (frame !== 0) {
      cancel(frame);
    }

    browserWindow.removeEventListener("resize", refresh);
    browserWindow.removeEventListener("scroll", refresh);
    observer?.disconnect();
    delete widget.dataset.ptcChatbotPlacementBound;
  };
};
